<template>
  <div class="tree-container">
    <TreeItem
      v-for="option in modelTreeRef.options"
      :key="getDescendantProp(option, props___.identificatorKey)"
      v-bind="{
        label: getDescendantProp(option, props___.labelKey),
        active: getDescendantProp(option, props___.activeKey),
        children: getDescendantProp(option, props___.childrenKey),
        identificator: getDescendantProp(option, props___.identificatorKey),
        labelKey: props___.labelKey,
        activeKey: props___.activeKey,
        childrenKey: props___.childrenKey,
        identificatorKey: props___.identificatorKey,
        legend: option.legend
      }"
      @change-active="onChangeActive"
      @set-transparency="setTransparency"
    >
    </TreeItem>
  </div>
</template>


<script setup>
import {watch, toRaw} from 'vue';
import TreeItem from './TreeItem.vue';
import TreeModel from './tree.component-model';

import {
  useImmer,
} from '@sttedra_ui/framework';

// #region Props, Emits
const props___ = defineProps({
  options: {
    type: Array,
    default: () => {
      return [];
    },
  },
  labelKey: {
    type: String,
    default: 'label',
  },
  activeKey: {
    type: String,
    default: 'active',
  },
  childrenKey: {
    type: String,
    default: 'children',
  },
  identificatorKey: {
    type: String,
    default: 'id',
  },
});
const emits = defineEmits(['itemSelected', 'change', 'setTransparency']);
// #endregion


// #region Params, Ref, Reactives, Computed
const modelTree = new TreeModel(toRaw(props___));
const {
  ref: modelTreeRef,
  update: updateModelTreeRef,
} = useImmer(modelTree);
// #endregion


// #region Functions
function setTransparency(params) {
  emits('setTransparency', params);
}
function updateModelTree(values) {
  updateModelTreeRef((draft) => {
    draft.update(values);
    return draft;
  });
}

/**
 * Event when a item has been changed his status of active
 *
 * @param {Object} item
 *
 * @emit {Array} options
 */
function onChangeActive(item) {
  const options = updateStatusOption(item, modelTreeRef.value.options);
  emits('change', options);
  updateModelTree({options});
}

/**
 * Update the status of the item
 *
 * Check if the item is in the loop, if the item is not the item of the loop
 * check if it has children and it do a recursive call.
 *
 * @param {Object} item
 * @param {Array} options
 *
 * @eemit {Object} option
 *
 * @return {Array}
 */
function updateStatusOption(item, options) {
  return options.map((option) => {
    const children = getDescendantProp(option, props___.childrenKey);
    // If is the item selected
    if (item.identificator === getDescendantProp(option, props___.identificatorKey)) {
      // New status if is the item selected
      const newStatusActive = !getDescendantProp(option, props___.activeKey);
      option = setDescendantProp(option, props___.activeKey, newStatusActive);
      emits('itemSelected', option);
    } // eslint-disable-line brace-style
    // Else if has children
    else if (children && children.length > 0) {
      const newChildren = updateStatusOption(item, children);
      option = setDescendantProp(option, props___.childrenKey, newChildren);
      const newStatusActive = checkAllChildAreSelecteds(newChildren);
      option = setDescendantProp(option, props___.activeKey, newStatusActive);
    }


    if (children && children.length > 0 &&
        item.children &&
        item.children.length > 0 &&
        (
          // Raster layers
          (!!children[0]?.values?.id && !!item?.children[0]?.values?.id &&
          children[0].values.id == item.children[0].values.id) ||
          // Vector layers
          (!!children[0]?.id && !!item?.children[0]?.id &&
            children[0].id == item.children[0].id)
        )
    ) {
      const active = getDescendantProp(option, props___.activeKey);
      setItemsActive(active, children);
    }
    return option;
  });
}

/**
 * Recursive function to set the status active to item
 *
 * @param {Boolean} value
 * @param {Array} options
 */
function setItemsActive(value, options) {
  options.forEach((option) => {
    const children = getDescendantProp(option, props___.childrenKey);

    if (children && children.length > 0) {
      setItemsActive(value, children);
    }
    option = setDescendantProp(option, props___.activeKey, value);
    emits('itemSelected', option);
  });
}

/**
 * Return if a item has all the items selecteds
 *
 * @param {Array} options
 *
 * @return {Boolean}
 */
function checkAllChildAreSelecteds(options) {
  let allSelected = true;
  for (let i = 0; i < options.length && allSelected; i++) {
    const option = options[i];
    const children = getDescendantProp(option, props___.childrenKey);
    if (!getDescendantProp(option, props___.activeKey)) {
      allSelected = false;
    }
    if (allSelected && !!children?.length) {
      allSelected = allSelected && checkAllChildAreSelecteds(children);
    }
  }
  return allSelected;
}

/**
 * Find the property of the object and it is returned
 *
 * @param {Object} obj
 * @param {String} desc
 *
 * @return {Any}
 */
function getDescendantProp(obj, desc) {
  const arr = desc.split('.');
  while (arr.length && (obj = obj[arr.shift()]));
  return obj;
}

/**
 * Set the value to the propertie of object
 *
 * @param {Object} obj
 * @param {String} desc
 * @param {Any} value
 *
 * @return {Object}
 */
function setDescendantProp(obj, desc, value) {
  const arr = desc.split('.');
  obj = setValue(obj, arr, value);
  return obj;
}

/**
 * Recursive function to set the value of object
 *
 * @param {Object} obj
 * @param {String} properties
 * @param {Any} value
 *
 * @return {Object}
 */
function setValue(obj, properties, value) {
  if (properties.length > 1) {
    obj[properties[0]] = setValue(
      obj[properties[0]],
      properties.slice(1, properties.length),
      value,
    );
  } else {
    obj[properties[0]] = value;
  }
  return obj;
}
// #endregion


// #region Effects, Hooks
watch(props___, () => {
  updateModelTree(props___);
});
// #endregion


// #region Provide, Expose
// ...
// #endregion
</script>


<style lang="scss" scoped>
.tree-container {
  overflow: auto;
  height: 70dvh;
}
</style>
